import { type CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { type ActionFunctionArgs, redirect } from "react-router";
import { useActionData, useSubmit } from "react-router";
import { Card, CardContent } from "~/components/ui/Card";
import { authenticator } from "~/services/auth.server";
import { sessionStorage } from "~/services/session.server";

const ERROR_MESSAGES = {
  LOGIN_FAILED: "ログインに失敗しました。もう一度お試しください。",
  ADMIN_ROLE_REQUIRED: "アクセス権限がありません。管理者にお問い合わせください。",
} as const;

export const action = async ({ request }: ActionFunctionArgs) => {
  try {
    const authData = await authenticator.authenticate("google", request);
    if (!authData) {
      return redirect("/login");
    }
    const session = await sessionStorage.getSession(request.headers.get("cookie"));
    session.set("user", authData);
    return redirect("/", {
      headers: {
        "Set-Cookie": await sessionStorage.commitSession(session),
      },
    });
  } catch (error) {
    if (error instanceof Response) {
      try {
        const errorData = (await error.json()) as { message?: string };
        const errorMessage = errorData.message || ERROR_MESSAGES.LOGIN_FAILED;

        if (errorMessage.includes("[ADMIN_ROLE_REQUIRED]")) {
          return {
            error: ERROR_MESSAGES.ADMIN_ROLE_REQUIRED,
          };
        }

        return {
          error: errorMessage,
        };
      } catch {
        return {
          error: ERROR_MESSAGES.LOGIN_FAILED,
        };
      }
    }

    return {
      error: ERROR_MESSAGES.LOGIN_FAILED,
    };
  }
};

export default function Login() {
  const submit = useSubmit();
  const actionData = useActionData<typeof action>();

  const handleSuccess = (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      return;
    }

    const formData = new FormData();
    formData.append("credential", credentialResponse.credential);
    submit(formData, { method: "POST", action: "/login" });
  };

  const handleError = () => {};

  return (
    <div className="h-screen w-screen flex flex-col justify-center items-center bg-slate-100 gap-4">
      <div className="flex flex-col items-center justify-center">
        <img src="/cytometa-logo.svg" alt="Cytometa" className="w-48" />
      </div>
      <Card className="px-8">
        <CardContent className="px-auto h-32 justify-center flex flex-col">
          {actionData?.error && <div className="text-red-600 text-sm mb-4 text-center">{actionData.error}</div>}
          <div className="flex w-full items-center justify-center">
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={handleError}
              text="signin_with"
              shape="rectangular"
              theme="outline"
              size="large"
              locale="ja"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
